import { BlogPost } from '../types';

const post: BlogPost = {
	slug: 'afkar-hadaya-eid-al-ism-lil-atfal',
	title: 'أفكار هدايا عيد الاسم للأطفال (ولماذا يكون الاسم نفسه هو الهدية)',
	description:
		'ماذا تُهدي طفلاً في عيد اسمه: كيف تعمل هذه العادة في اليونان وخارجها، ولماذا تكون الهدايا المبنية حول الاسم نفسه هي الأنسب، وما الذي يجب تجنبه.',
	date: '2026-08-04',
	heroImage: '/assets/hero/kid-5/classic_scene.png',
	tags: ['أدلة الهدايا', 'أعياد الاسم', 'تقاليد يونانية'],
	body: `في اليونان، قد يقابل سؤالك عن تاريخ ميلاد أحدهم بهزة كتفين. أما سؤالك عن «عيد اسمه» فيحصل على جواب فوري، لأن الجميع يعرفه — إنه مطبوع على التقويم. ولمن لا يعرف هذه العادة: في التقليد الأرثوذكسي اليوناني، يُخصَّص لكل اسم يوم في السنة يوافق ذكرى القدّيس الذي يحمل الاسم نفسه، ويحتفل به من يحمل ذلك الاسم كل عام في ذلك التاريخ الثابت. وفي كثير من البيوت اليونانية يكون عيد الاسم مناسبة أكبر من عيد الميلاد نفسه: بيت مفتوح، حلويات على الطاولة، وأقارب يصلون دون موعد مسبق ويُرحَّب بهم تماماً.

وهذا يطرح سؤال هدايا لا يعرفه بقية العالم: ماذا تُحضر لطفل في يوم عيد *اسمه*؟

## منطق هدية عيد الاسم

عيد الميلاد يحتفل بوجود الطفل. أما عيد الاسم فيحتفل بشيء أكثر تحديداً — الاسم نفسه، والقدّيس الذي حمله، والجَد أو الجدة اللذين غالباً ما جاء منهما، وسلسلة كاملة من حاملي الاسم عبر أجيال العائلة.

وهذا يشير إلى الجواب. هدية عيد الميلاد يمكن أن تكون أي شيء؛ أما هدية عيد الاسم فتكون في أفضل حالاتها حين تدور *حول الاسم نفسه*. إنها المناسبة الوحيدة التي لا تُعدّ فيها هدية مبنية على اسم الطفل مجرد حيلة تخصيص — بل هي فعلياً موضوع اليوم بأكمله.

## ما الذي ينجح

**كتاب قصص مخصص يكون فيه الاسم هو النجم.** السقف الواضح لهذه الفئة. اسم الطفل يجري عبر القصة كلها، ملتصقاً ببطل مرسوم يشبهه. في اليوم الذي تحتفل فيه العائلة بالاسم، يحصل الطفل على كتاب يقوم فيه الاسم بأشياء. للعائلات اليونانية، يمكن [إنتاج القصة باللغة اليونانية](/personalized-greek-books-for-kids) — مع صياغة الاسم وتصريفه بشكل صحيح نحوياً، لأن قصة عن نيكوس (Νίκος) يجب أن تعرف متى تقول «تو نيكو» (του Νίκου)، وهي صيغة الملكية في اليونانية، تماماً كما يتغير شكل الاسم بالعربية بين «نيكوس» و«لنيكوس» بحسب موقعه في الجملة.

**أي شيء يُظهر الاسم بشكل متقن.** لوحة لباب غرفة النوم، كوب بشخصيته المرسومة. المعيار الفاصل: يجب أن يكون الاسم جزءاً من التصميم، لا مجرد ملصق أُضيف كفكرة متأخرة.

**الحلويات التقليدية، إضافة إلى شيء يدوم.** يأتي زوار عيد الاسم تقليدياً ومعهم حلويات أو هدية رمزية. أن تكوني الزائرة التي أحضرت *أيضاً* الشيء الذي يحمل اسم الطفل هي طريقة رخيصة لتكوني العمّة المفضّلة.

**لأبناء الجاليات: طرد رعاية لعيد الاسم.** الأطفال الذين يكبرون خارج اليونان غالباً ما يجهلون تماماً أن لديهم عيد اسم. هدية تصل خصيصاً في ذلك اليوم — وخاصة كتاب باليونانية عنهم — هي الطريقة التي تنجو بها هذه العادة من عبور الحدود.

## ما يجب تجنبه

- **الألعاب العامة.** لا عيب فيها، لكنها تُفوّت الفكرة الوحيدة التي تحملها هذه المناسبة.
- **عيد ميلاد ثانٍ.** عيد الاسم ليس عيد ميلاد بنسخة ثانية — إنه أصغر وأكثر دفئاً وأقرب إلى العائلة منه إلى قائمة أمنيات الطفل. شيء واحد ذو معنى أفضل من كومة كاملة.
- **ملابس رسمية منقوشة بالاسم.** الإطار الفضي المحفور عليه الاسم هو الخيار التقليدي المعتاد، وينتهي به المطاف مباشرة على رف لا ينظر إليه الطفل أبداً.

## التوقيت هنا أكثر تسامحاً من أعياد الميلاد

أعياد الاسم ثابتة التاريخ — فعيد القديس ديمتريوس (Αγίου Δημητρίου) يوافق السادس والعشرين من أكتوبر كل عام، بلا أي تفاوض على موعد الحفلة. هذا يجعلها أسهل موعد نهائي للهدايا في السنة كلها من ناحية التخطيط، وأسهله من ناحية التبكير فيه.

الهدايا المخصصة تُصنع حسب الطلب (رسم، موافقة، طباعة، شحن)، لذا فإن التبكير بأسبوعين إلى ثلاثة أسابيع أمر مريح. التقويم علني ومعروف؛ فلا عذر هنا كما هو الحال مع أعياد الميلاد التي قد تُفاجئك.

## إن لم تكن يونانياً لكن الطفل يوناني

الزواج من عائلة يونانية أو مصادقتها يعني أن تتعلم أن تجاهل عيد الاسم أمر يُلاحَظ. إن لم تستطع الحضور: هدية تصل في ذلك اليوم تقوم مقام الزيارة نيابة عنك. كتاب عن الطفل، بلغة أجداده، يُرسل احتفاءً بعيد اسمه — تلك ليست هدية ينساها طفل، والأهم من ذلك، لا تنساها الجدة (yiayia) أيضاً.`,
	faqs: [
		{
			question: 'ماذا تُهدي طفلاً في عيد اسمه؟',
			answer:
				'هدايا عيد الاسم تكون تقليدياً أصغر من هدايا عيد الميلاد، وأفضلها ما يرتبط بالاسم نفسه — موضوع ذلك اليوم. كتاب قصص مخصص يجري فيه اسم الطفل عبر القصة، ويُفضّل أن يكون باللغة اليونانية للعائلات اليونانية، يلائم المناسبة تماماً. ويُحضر الزوار تقليدياً حلويات أيضاً.',
		},
		{
			question: 'هل عيد الاسم أهم من عيد الميلاد في اليونان؟',
			answer:
				'بالنسبة لكثير من العائلات، هو على الأقل بنفس الأهمية. أعياد الاسم علنية — يعرف الجميع تاريخها من التقويم — وتُحتفل تقليدياً ببيت مفتوح وزوار، بينما تكون أعياد الميلاد مناسبات أكثر خصوصية.',
		},
		{
			question: 'متى يجب أن أطلب هدية عيد اسم مخصصة؟',
			answer:
				'قبل أسبوعين إلى ثلاثة أسابيع. أعياد الاسم تقع في تواريخ ثابتة كل عام، ما يجعلها أسهل موعد هدايا نهائي في السنة للتخطيط له.',
		},
	],
	lang: 'ar',
	dateLocale: 'ar-EG',
	ui: {
		homeLabel: 'الرئيسية',
		blogLabel: 'المدونة',
		faqHeading: 'الأسئلة الشائعة',
		moreReadingHeading: 'مقالات أخرى',
		ctaLabel: 'تصفح الهدايا المخصصة',
	},
};

export default post;
